import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TicketResponse } from 'accesso-model-lib';

import { Card, PhoneType } from '../api/responses/payment-portal-login';
import { HistoricPayment } from './user.models';
import { OrderTicketType, UserState } from './user.state';
import { ContractViewerDetails } from 'src/app/pages/account/contract-viewer/models/contract.models';

export interface Tokens {
	cToken: string;
	eToken?: string;
	oToken: string;
	pToken?: string;
	utToken?: string;
	uidToken?: string;
}

export const selectUser = createFeatureSelector<UserState>('user');

export const selectMfaCode = createSelector(selectUser, (user: UserState) => user.mfaCode);

export const selectUserToken = createSelector(selectUser, (user: UserState) => user.userToken ?? '');

export const selectCepayMerchantId = createSelector(selectUser, (user: UserState) => user.user?.cepayMerchantId);

export const selectJwt = createSelector(selectUser, (user: UserState) => user.user?.jwt ?? '');

export const selectMfaExpiration = createSelector(selectUser, (user: UserState) => user.mfaExpiration);

export const selectCurrentPaymentType = createSelector(
	selectUser,
	(user: UserState): string => user.user?.currentPaymentType
);

export const selectUserTickets = createSelector(selectUser, (user: UserState): TicketResponse[] => user?.user?.tickets);

export const selectIsCancelled = createSelector(selectUser, (user: UserState): boolean => user.isCancelled);

export const selectIsCancellationSet = createSelector(selectUser, (user: UserState): boolean => user.isCancellationSet);

export const selectIsDelinquent = createSelector(
	selectUser,
	(user: UserState): boolean => user.user?.isDelinquent ?? false
);

export const selectParentEConsignmentMerchantId = createSelector(
	selectUser,
	(user: UserState): string => user?.user?.parentEConsignmentMerchantId
);

export const selectIsAuthenticating = createSelector(selectUser, (user: UserState): boolean => user.isAuthenticating);

export const selectIsAuthenticated = createSelector(selectUser, (user: UserState): boolean => user.isAuthenticated);

export const selectPaymentHistory = createSelector(
	selectUser,
	(user: UserState): HistoricPayment[] => user.user?.paymentHistory || []
);

export const selectIsMembership = createSelector(
	selectUser,
	(user: UserState): boolean => user.user?.packageType === 'MEM'
);

export const selectIsPaymentPlan = createSelector(
	selectUser,
	(user: UserState): boolean => !!user.user?.scheduledPayments?.SP?.length
);

export const selectCards = createSelector(selectUser, (user: UserState): Card[] => user.user?.cards || []);

export const selectUserDemographics = createSelector(selectUser, (userState: UserState) => {
	return userState.user?.details || [];
});

export const selectTokens = createSelector(selectUser, (userState: UserState): Tokens => {
	if (!userState.isAuthenticated) {
		return {
			cToken: '',
			eToken: '',
			oToken: '',
			pToken: '',
		};
	}

	const details = userState.user.details?.[0];
	const { oToken, cToken } = userState.user;
	const email = details?.email;
	const pToken = details?.phone?.find((p) => p.type === PhoneType.HOME)?.pToken ?? details?.phone?.[0]?.pToken;

	return {
		cToken: details?.cToken ?? cToken,
		eToken: email?.[0]?.eToken,
		oToken,
		pToken,
		uidToken: userState.uidToken,
		utToken: userState.utToken,
	};
});

export const selectIsLockoutFlow = createSelector(selectUser, (userState: UserState) => {
	return userState.isLockoutAccess;
});

export const selectUIdToken = createSelector(selectUser, (user: UserState) => user.uidToken ?? null);

export const selectOrderTicketTypes = createSelector(
	selectUser,
	(user: UserState): OrderTicketType => user.orderTicketTypes
);

export const selectContractViewerDetails = createSelector(
	selectUser,
	(user: UserState): ContractViewerDetails => user.user?.contractViewerDetails || null
);

export const selectGuestViewOrderComplete = createSelector(
	selectUser,
	(user: UserState): boolean => user.guestViewOrderComplete || null
);
